import React, { useEffect, useRef, useState } from 'react';
import { getAddBatchStatus, runAddBatch } from '../../services/api';
import { notifyProgress } from '../../services/progressEvent';
import { track, EVENTS } from '../../services/analytics';

// ============================================================================
// AN ASTRA ADD'S ANALYSIS, AS ONE LINE (#2942)
//
// Founder 2026-10-02: "we don't need that progress bar in the chat if we
// added it to the launch pad", then picked mock A: one live line under the
// confirm, no bar. The payoff ("University of Florida is a reach") arrives
// where the student asked; the Launchpad cards carry the step-by-step state
// for anyone who goes there (desktop Astra is its own page and cannot see
// the Launchpad, which is why the chat keeps this line).
//
// Rules (carried from #2938's bar):
//   - every state is the SERVER's batch status; nothing advances on a timer,
//     and nothing reads "ready" before the batch says so.
//   - a school that fails says so and where to retry; the line still settles.
//   - if the status can't be read, say where to look instead of spinning.
//   - a stalled run (the counselor's kick never arrived, or the run died
//     and its lease lapsed) is asked for again HERE as well as on the
//     Launchpad: desktop /astra and the drawer over other pages never show
//     the Launchpad (review 2026-10-02), and the server lease makes a
//     duplicate ask a no-op.
// ============================================================================

// Every 2 s for the first two minutes (a school typically finishes in
// 10-20 s), then every 5 s. The give-up window outlasts profile-manager's
// run lease (600 s).
const POLL_MS = 2000;
const SLOW_POLL_MS = 5000;
const FAST_FOR_MS = 2 * 60 * 1000;
const GIVE_UP_MS = 12 * 60 * 1000;
const MAX_READ_ERRORS = 5;
const RUN_REASK_MS = 30000;

// The band as a phrase. SUPER_REACH stays its own word: it is a different
// claim from "a reach", and the card says "Super Reach".
const BAND_PHRASE = { SUPER_REACH: 'a super reach', REACH: 'a reach', TARGET: 'a target', SAFETY: 'a safety' };

const listNames = (names) => (names.length <= 2
    ? names.join(' and ')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

const readOf = (s) => (BAND_PHRASE[s.fit_category]
    ? `${s.name} is ${BAND_PHRASE[s.fit_category]}`
    : `${s.name} is ready`);

/** The line for a batch, from its server state only. */
export function statusLine(batch) {
    const schools = batch?.schools || [];
    const n = schools.length;
    const ready = schools.filter((s) => s.state === 'ready');
    const failed = schools.filter((s) => s.state === 'failed');
    const running = schools.filter((s) => s.state !== 'ready' && s.state !== 'failed');
    const reads = ready.map(readOf).join(', ');
    if (batch?.done) {
        if (!failed.length) {
            const lead = n === 1 ? 'Ready' : n === 2 ? 'Both ready' : `All ${n} ready`;
            return `${lead}: ${reads}.`;
        }
        // #4064: an empty record gets no band; "retry" can't help until it has one.
        const empty = failed.length && failed.every((s) => s.reason === 'profile_incomplete');
        const missed = empty
            ? `${listNames(failed.map((s) => s.name))} couldn't be analyzed yet: add your GPA, courses or activities to your profile first.`
            : `${listNames(failed.map((s) => s.name))} couldn't be analyzed. Retry on your Launchpad.`;
        return reads ? `${reads}. ${missed}` : missed;
    }
    if (!ready.length && !failed.length) {
        return n === 1 ? `Analyzing fit for ${schools[0].name}…` : `Analyzing fit for ${n} colleges…`;
    }
    const failedNote = failed.length ? ` ${listNames(failed.map((s) => s.name))} couldn't be analyzed.` : '';
    return `${reads ? `${reads}.` : ''}${failedNote} Still analyzing ${listNames(running.map((s) => s.name))}…`.trim();
}

export default function AddStatusLine({ email, progress, confirmedAt }) {
    const [batch, setBatch] = useState(progress);
    const [lost, setLost] = useState(false);
    const errors = useRef(0);
    const lastAsk = useRef(0);

    useEffect(() => {
        if (!progress?.batch_id || progress.done) return undefined;
        let cancelled = false;
        let timer = null;
        const started = Date.now();
        const tick = async () => {
            if (cancelled) return;
            try {
                const res = await getAddBatchStatus(email, progress.batch_id);
                if (cancelled) return;
                if (res?.success && res.batch) {
                    errors.current = 0;
                    setBatch(res.batch);
                    if (res.batch.done) return;
                    if (res.batch.stalled && Date.now() - lastAsk.current > RUN_REASK_MS) {
                        lastAsk.current = Date.now();
                        runAddBatch(email, progress.batch_id).catch(() => {});
                    }
                } else {
                    errors.current += 1;
                }
            } catch {
                errors.current += 1;
            }
            if (errors.current >= MAX_READ_ERRORS || Date.now() - started > GIVE_UP_MS) {
                setLost(true);
                return;
            }
            timer = setTimeout(tick, Date.now() - started < FAST_FOR_MS ? POLL_MS : SLOW_POLL_MS);
        };
        timer = setTimeout(tick, POLL_MS);
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
    }, [email, progress?.batch_id, progress?.done]);

    useEffect(() => {
        // Fires once, when the batch settles; polling has stopped by then.
        if (!batch?.done) return;
        track(EVENTS.ASTRA_ADD_ANALYSED, {
            schools: batch.schools?.length || 0, ready: batch.ready, failed: batch.failed,
            ms: typeof confirmedAt === 'number' ? Date.now() - confirmedAt : null,
        });
        // The page behind the chat now has fits it didn't have.
        notifyProgress();
    }, [batch?.done, batch?.ready, batch?.failed, batch?.schools?.length, confirmedAt]);

    if (!batch?.schools?.length) return null;
    const settled = Boolean(batch.done);
    const text = !settled && lost
        ? "Couldn't check on the analysis just now. Your Launchpad shows each college as it finishes."
        : statusLine(batch);
    return (
        <p data-testid="concierge-add-status" data-done={settled ? 'true' : 'false'}
           role="status" aria-live="polite"
           className={`mt-1.5 flex items-start gap-2 text-xs ${settled ? 'font-semibold text-[#1A4D2E]' : 'text-[#4A4A4A]'}`}>
            {!settled && !lost && (
                <span aria-hidden="true"
                      className="mt-0.5 h-3 w-3 flex-none rounded-full border-2 border-[#D6E8D5] border-t-[#1A4D2E] motion-safe:animate-spin" />
            )}
            {settled && <span aria-hidden="true">✓</span>}
            <span>{text}</span>
        </p>
    );
}
